import {isNullish} from '@dovca/toolbox';
import {ProductAvailabilityColor, ProductAvailabilityKind} from '~/utils/productAvailability/enums';
import type {Gsoa_ProductCatalog_ProductDetail as ProductDetail} from '~/openapi';

export abstract class ProductAvailability {
	abstract kind: ProductAvailabilityKind;

	abstract get color(): ProductAvailabilityColor;

	abstract get description(): string;

	abstract get isAvailable(): boolean | undefined;

	static getAvailability(product: ProductDetail): ProductAvailability {
		const kind = ProductAvailability.getProductAvailabilityKind(product);

		try {
			switch (kind) {
				case ProductAvailabilityKind.Rough:
					return new RoughProductAvailability(product);
				case ProductAvailabilityKind.Precise:
					return new PreciseProductAvailability(product);
				case ProductAvailabilityKind.Fictive:
					return new FictiveProductAvailability();
				default:
					return new UnknownProductAvailability();
			}
		} catch (error) {
			return new UnknownProductAvailability();
		}
	}

	private static getProductAvailabilityKind(product: ProductDetail): ProductAvailabilityKind | null {
		switch (true) {
			case product.hasFictiveStock:
				return ProductAvailabilityKind.Fictive;
			case !!product.productInHouse?.placements?.length:
				return ProductAvailabilityKind.Rough;
			case product.productInHouse?.stockAmount !== null:
				return ProductAvailabilityKind.Precise;
			default:
				return ProductAvailabilityKind.Unknown;
		}
	}
}

export class RoughProductAvailability extends ProductAvailability {
	kind = ProductAvailabilityKind.Rough;

	constructor(protected product: ProductDetail) {
		if (isNullish(product.productInHouse?.stockAmount)) {
			throw new Error('Product has no stock amount', {cause: {product}});
		}

		super();
	}

	get color(): ProductAvailabilityColor {
		switch (this.roughState) {
			case 'inStock':
				return ProductAvailabilityColor.InStock;
			case 'almostOutOfStock':
				return ProductAvailabilityColor.AlmostOutOfStock;
			default:
				return ProductAvailabilityColor.OutOfStock;
		}
	}

	get description(): string {
		switch (this.roughState) {
			case 'inStock':
				return 'Skladem';
			case 'almostOutOfStock':
				return 'Brzy se vyprodá';
			default:
				return 'Vyprodáno';
		}
	}

	get isAvailable(): boolean {
		return this.roughState !== 'outOfStock';
	}

	private get roughState(): 'inStock' | 'almostOutOfStock' | 'outOfStock' {
		// Sum presentation stock across all placements
		const totalPresentationStock = this.product.productInHouse!.placements!
			.map((placement) => placement.presentationStock || 0)
			.reduce((acc, stock) => acc + stock, 0);

		// Calculate the stock ratio
		const stockRatio = this.product.productInHouse!.stockAmount! / totalPresentationStock;

		switch (true) {
			case stockRatio >= 0.2:
				return 'inStock';
			case stockRatio < 0.2 && stockRatio > 0.05:
				return 'almostOutOfStock';
			default:
				return 'outOfStock';
		}
	}
}

export class PreciseProductAvailability extends ProductAvailability {
	kind = ProductAvailabilityKind.Precise;

	constructor(protected product: ProductDetail) {
		if (isNullish(product.productInHouse?.stockAmount)) {
			throw new Error('Product has no stock amount', {cause: {product}});
		}

		super();
	}

	get color(): ProductAvailabilityColor {
		return this.isAvailable
			? ProductAvailabilityColor.InStock
			: ProductAvailabilityColor.OutOfStock;
	}

	get description(): string {
		switch (true) {
			case !this.isAvailable:
				return 'Vyprodáno';
			case this.product.productInHouse!.stockAmount! >= 100:
				return 'Skladem více než 100 ks';
			case this.product.productInHouse!.stockAmount! >= 10:
				return 'Skladem více než 10 ks';
			case this.product.productInHouse!.stockAmount! > 3:
				return 'Skladem méně než 10 ks';
			case this.product.productInHouse!.stockAmount! > 0:
				return 'Skladem 1–3 ks';
			default:
				return 'Vyprodáno';
		}
	}

	get isAvailable(): boolean {
		return this.product.productInHouse!.stockAmount! > 0;
	}
}

export class FictiveProductAvailability extends ProductAvailability {
	kind = ProductAvailabilityKind.Fictive;

	get color(): ProductAvailabilityColor {
		return ProductAvailabilityColor.InStock;
	}

	get description(): string {
		return 'Běžně dostupné na prodejně';
	}

	get isAvailable(): boolean {
		return true;
	}
}

export class UnknownProductAvailability extends ProductAvailability {
	kind = ProductAvailabilityKind.Unknown;

	get color(): ProductAvailabilityColor {
		return ProductAvailabilityColor.Unknown;
	}

	get description(): string {
		return 'Informace o skladové dostupnosti nejsou k dispozici';
	}

	get isAvailable(): undefined {
		return undefined;
	}
}
